package org.example.project.planner

import org.example.project.client.model.ActivityResponse
import org.example.project.client.model.BlockType
import org.example.project.client.model.EventBlockResponse
import org.example.project.client.model.EventResponse

sealed interface SaveStatus {
    object Idle : SaveStatus
    object Saving : SaveStatus
    object Saved : SaveStatus // "Zapisano"
    data class Error(val message: String) : SaveStatus
    data class Conflict(val message: String) : SaveStatus // 409 Conflict - keeps local form intact
}

sealed interface PlannerViewMode {
    object EventList : PlannerViewMode
    data class EventDetail(val eventId: String) : PlannerViewMode
    object CreateEvent : PlannerViewMode
}

enum class TimePickerTarget {
    EVENT_START,
    EVENT_END
}

fun timeToMinutes(timeStr: String): Int {
    val trimmed = timeStr.trim()
    val parts = trimmed.split(":")
    if (parts.size != 2) return 0
    val h = parts[0].toIntOrNull() ?: return 0
    val m = parts[1].toIntOrNull() ?: return 0
    return (h.coerceIn(0, 23) * 60 + m.coerceIn(0, 59))
}

fun minutesToTime(totalMinutes: Int): String {
    val clamped = totalMinutes.coerceIn(0, 23 * 60 + 59)
    val h = clamped / 60
    val m = clamped % 60
    return "${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}"
}

const val MAX_5MIN_GRID_MINUTES = 23 * 60 + 55 // 1435, i.e. 23:55
const val MAX_15MIN_GRID_MINUTES = 23 * 60 + 45 // 1425, i.e. 23:45

fun snapTo5Minutes(minutes: Int): Int {
    if (minutes <= 0) return 0
    val snapped = ((minutes + 2) / 5) * 5
    return snapped.coerceIn(0, MAX_5MIN_GRID_MINUTES)
}

fun ceilTo5Minutes(minutes: Int): Int {
    if (minutes <= 0) return 0
    val rem = minutes % 5
    return if (rem == 0) minutes else minutes + (5 - rem)
}

fun floorTo5Minutes(minutes: Int): Int {
    if (minutes <= 0) return 0
    val clamped = minutes.coerceAtMost(MAX_5MIN_GRID_MINUTES)
    val rem = clamped % 5
    return (clamped - rem).coerceAtLeast(0)
}

fun hasValid5MinuteSlot(eventStartMin: Int, eventEndMin: Int): Boolean {
    val minGridStart = ceilTo5Minutes(eventStartMin)
    val maxGridEnd = floorTo5Minutes(eventEndMin)
    return maxGridEnd - minGridStart >= 5
}

fun snapTo15Minutes(minutes: Int): Int {
    if (minutes <= 0) return 0
    val snapped = ((minutes + 7) / 15) * 15
    return snapped.coerceIn(0, MAX_15MIN_GRID_MINUTES)
}

fun createDefaultDraft(id: String = generateClientUuid()): EventFormDraft = EventFormDraft(
    id = id,
    version = 1L,
    name = "Nowe wydarzenie",
    date = null,
    startTime = "10:00",
    endTime = "16:00",
    blocks = emptyList()
)

fun isUntouchedDraft(draft: EventFormDraft): Boolean =
    draft.name.trim() == "Nowe wydarzenie" &&
        draft.blocks.isEmpty() &&
        draft.startTime.trim() == "10:00" &&
        draft.endTime.trim() == "16:00" &&
        draft.date.isNullOrBlank()

data class BlockDraft(
    val id: String = "",
    val blockType: BlockType = BlockType.CATALOG_GAME,
    val title: String = "",
    val startTime: String = "",
    val endTime: String = "",
    val instructions: String = "",
    val description: String = "",
    val notes: String = "",
    val activityId: String? = null
)

data class EventFormDraft(
    val id: String = "",
    val version: Long = 1L,
    val name: String = "",
    val date: String? = null,
    val startTime: String = "",
    val endTime: String = "",
    val blocks: List<BlockDraft> = emptyList()
)

data class PlannerUiState(
    val viewMode: PlannerViewMode = PlannerViewMode.CreateEvent,
    val isLoading: Boolean = false,
    val events: List<EventResponse> = emptyList(),
    val currentEvent: EventResponse? = null,
    val eventDraft: EventFormDraft = createDefaultDraft(),
    val favorites: List<ActivityResponse> = emptyList(),
    val favoriteIds: Set<String> = emptySet(),
    val catalogActivities: List<ActivityResponse> = emptyList(),
    val isCatalogLoading: Boolean = false,
    val isCatalogPickerOpen: Boolean = false,
    val isAddTypePickerOpen: Boolean = false,
    val pendingBlockStartTime: String = "10:00",
    val pendingBlockEndTime: String = "11:00",
    val isTimePickerOpen: Boolean = false,
    val timePickerTarget: TimePickerTarget? = null,
    val editingBlock: BlockDraft? = null,
    val isBlockEditorOpen: Boolean = false,
    val isFavoritesPickerOpen: Boolean = false,
    val isTimelineEditMode: Boolean = false,
    val selectedBlockId: String? = null,
    val saveStatus: SaveStatus = SaveStatus.Idle,
    val validationError: String? = null,
    val generalError: String? = null
)

fun EventBlockResponse.toDraft(): BlockDraft = BlockDraft(
    id = id,
    blockType = blockType,
    title = title,
    startTime = startTime,
    endTime = endTime,
    instructions = instructions ?: "",
    description = description ?: "",
    notes = notes ?: "",
    activityId = activityId
)

fun EventResponse.toDraft(): EventFormDraft = EventFormDraft(
    id = id,
    version = version,
    name = name,
    date = date,
    startTime = startTime,
    endTime = endTime,
    blocks = blocks.map { it.toDraft() }
)

